import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  type TooltipProps,
} from "recharts";
import type { Annotation, DailyPoint } from "../lib/types";
import { toChartMarkers, type ChartMarker } from "../lib/annotations";
import { shortDate } from "../lib/format";
import { COLORS } from "../lib/theme";

// Chart chrome — from the single hex source (Recharts needs literal hex).
const AXIS = COLORS.muted; // muted ticks/labels
const GRID = COLORS.grid; // gridline
const SURFACE = COLORS.surface; // tooltip background (= card surface)
const BORDER = COLORS.hairline; // hairline

interface Series {
  dataKey: keyof DailyPoint;
  color: string;
  seriesLabel: string;
}

interface Props {
  title: string;
  data: DailyPoint[];
  dataKey: keyof DailyPoint;
  color: string;
  /** Format a y value for ticks + tooltip. */
  format: (v: number) => string;
  /** Legend/label for the primary series (also the tooltip row label). */
  seriesLabel: string;
  /** Optional second line — adds a legend and multi-row tooltip. */
  secondary?: Series;
  /** Optional horizontal reference line (e.g. 1× break-even). */
  referenceY?: number;
  /** Change markers to overlay as vertical flags at their dates. */
  annotations?: Annotation[];
  /** Click a marker flag → open its detail (passes the marker's date). */
  onMarkerClick?: (date: string) => void;
}

/** Time line — single series by default; a legend appears when `secondary` is set. */
export function MetricLineChart({
  title,
  data,
  dataKey,
  color,
  format,
  seriesLabel,
  secondary,
  referenceY,
  annotations,
  onMarkerClick,
}: Props) {
  const keys = [dataKey, secondary?.dataKey].filter(Boolean) as (keyof DailyPoint)[];
  const hasData = data.some((d) =>
    keys.some((k) => typeof d[k] === "number" && (d[k] as number) > 0)
  );
  const markers = annotations?.length ? toChartMarkers(annotations) : [];

  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
        <h3 className="text-sm font-medium text-ink">{title}</h3>
      </div>
      <div className="h-56">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={shortDate}
                tick={{ fill: AXIS, fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: GRID }}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={(v) => format(Number(v))}
                tick={{ fill: AXIS, fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={64}
              />
              <Tooltip
                cursor={{ stroke: BORDER, strokeWidth: 1 }}
                content={<ChartTooltip format={format} />}
              />
              {secondary && (
                <Legend
                  verticalAlign="top"
                  height={22}
                  iconType="plainline"
                  wrapperStyle={{ fontSize: 11, color: AXIS }}
                />
              )}
              {referenceY != null && (
                <ReferenceLine
                  y={referenceY}
                  stroke={AXIS}
                  strokeDasharray="4 4"
                  label={{
                    value: "break-even",
                    position: "insideBottomRight",
                    fill: AXIS,
                    fontSize: 10,
                  }}
                />
              )}
              {markers.map((m) => (
                <ReferenceLine
                  key={m.date}
                  x={m.date}
                  stroke={m.color}
                  strokeOpacity={0.45}
                  strokeWidth={1.5}
                  ifOverflow="extendDomain"
                  label={<AnnotationFlag marker={m} onClick={onMarkerClick} />}
                />
              ))}
              <Line
                type="monotone"
                name={seriesLabel}
                dataKey={dataKey as string}
                stroke={color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                connectNulls
                isAnimationActive={false}
              />
              {secondary && (
                <Line
                  type="monotone"
                  name={secondary.seriesLabel}
                  dataKey={secondary.dataKey as string}
                  stroke={secondary.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                  connectNulls
                  isAnimationActive={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
            <div className="text-sm font-medium text-ink-secondary">Nothing to chart yet</div>
            <div className="text-xs text-muted">No {title.toLowerCase()} in this window.</div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A clickable change-marker flag at the top of the plot. Recharts injects
 * `viewBox` (the reference line's box) when this is used as a ReferenceLine
 * label. Hollow ring = an auto marker still needs a human "why"; solid = done.
 */
function AnnotationFlag(props: {
  viewBox?: { x: number; y: number; width: number; height: number };
  marker: ChartMarker;
  onClick?: (date: string) => void;
}) {
  const { viewBox, marker, onClick } = props;
  if (!viewBox) return null;
  const r = 6;
  return (
    <g
      transform={`translate(${viewBox.x}, ${viewBox.y})`}
      style={{ cursor: onClick ? "pointer" : "default" }}
      onClick={onClick ? () => onClick(marker.date) : undefined}
    >
      <title>{`${shortDate(marker.date)} — ${marker.label}${marker.annotated ? "" : " (needs a why)"}`}</title>
      {/* Larger transparent hit area for easy clicking. */}
      <circle cx={0} cy={0} r={13} fill="transparent" />
      <circle
        cx={0}
        cy={0}
        r={r}
        fill={marker.annotated ? marker.color : SURFACE}
        stroke={marker.color}
        strokeWidth={2}
      />
      {marker.count > 1 && (
        <text
          x={0}
          y={0}
          dy={3.5}
          textAnchor="middle"
          fontSize={9}
          fontWeight={700}
          fill={marker.annotated ? "#0B1416" : marker.color}
        >
          {marker.count}
        </text>
      )}
    </g>
  );
}

function ChartTooltip({
  active,
  payload,
  label,
  format,
}: TooltipProps<number, string> & { format: (v: number) => string }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => typeof p.value === "number");
  if (!rows.length) return null;
  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs shadow-lg"
      style={{ background: SURFACE, borderColor: BORDER }}
    >
      <div className="mb-1 font-medium text-ink">{label ? shortDate(String(label)) : ""}</div>
      {rows.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-ink-secondary">
          <span
            className="inline-block h-2 w-2 rounded-sm"
            style={{ background: (p.color as string) ?? "#fff" }}
          />
          {p.name}: <span className="tnum text-ink">{format(Number(p.value))}</span>
        </div>
      ))}
    </div>
  );
}
